import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Modal, ModalBody, ModalHeader,
} from 'reactstrap';
import { get, post } from '../../helpers/api_helper';

const money = (value) => `$${Number(value).toFixed(2)}`;
const errorText = (error) => error.response?.data?.error || 'Refund service unavailable. Please try again.';

export default function AmazonRefund({ sale, t, onSubmitted }) {
  const storageKey = `amazon-refund:${sale.id}`;
  const [attempt, setAttempt] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(storageKey)) || null;
    } catch (error) {
      return null;
    }
  });
  const [open, setOpen] = useState(false);
  const defaultQuantities = () => Object.fromEntries(
    (sale.items || []).map((item) => [item.id, String(item.qty)]),
  );
  const defaultAmount = () => (sale.total_paid == null ? '' : Math.max(0, Number(sale.total_paid) - 9.95).toFixed(2));
  const [quantities, setQuantities] = useState(defaultQuantities);
  const [amount, setAmount] = useState(defaultAmount);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const endpoint = `/api/sales/${sale.id}/amazon-refund`;

  const saveAttempt = (next) => {
    // Persist before sending so closing/reloading cannot lose an ambiguous request's key.
    if (next) sessionStorage.setItem(storageKey, JSON.stringify(next));
    else sessionStorage.removeItem(storageKey);
    setAttempt(next);
  };
  const invalidate = () => { setPreview(null); setError(''); };
  const run = async (action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const showResult = (response, pending) => {
    setResult(response);
    saveAttempt({ ...pending, requestId: response.request_id });
    onSubmitted();
  };
  const submit = (pending) => run(async () => {
    saveAttempt(pending);
    try {
      const response = await post(endpoint, pending.body, {
        headers: { 'Idempotency-Key': pending.key },
      });
      showResult(response, pending);
    } catch (failure) {
      if (failure.response?.data?.request_id) {
        showResult(failure.response.data, pending);
      } else if ([400, 404, 409, 422].includes(failure.response?.status)) {
        saveAttempt(null);
        setPreview(null);
        throw failure;
      } else {
        setError(t('Submission could not be confirmed. Check request status before starting another refund.'));
      }
    }
  });
  const checkStatus = () => {
    if (!attempt.requestId) {
      // Same key and exact body recover the original request without issuing it twice.
      submit(attempt);
      return;
    }
    run(async () => showResult(await get(`${endpoint}/${attempt.requestId}`), attempt));
  };
  const getPreview = (event) => {
    event.preventDefault();
    run(async () => {
      setPreview(null);
      const items = Object.entries(quantities).filter(([, qty]) => qty !== '')
        .map(([id, quantity]) => ({ sale_item_id: Number(id), quantity: Number(quantity) }));
      if (!items.length) {
        setError(t('Select at least one returned item.'));
        return;
      }
      const body = {
        items,
        ...(amount !== '' ? { amount } : {}),
      };
      const quote = await post(`${endpoint}/preview`, body);
      setPreview({ quote, body: { ...body, quote_token: quote.quote_token } });
    });
  };
  const quote = preview?.quote || result?.quote;
  const locked = busy || !!attempt;

  return (
    <div className="mt-2">
      <Button color="primary" size="sm" onClick={() => setOpen(true)}>{t('Refund on Amazon')}</Button>
      <Modal size="lg" isOpen={open} toggle={busy ? undefined : () => setOpen(false)}>
        <ModalHeader toggle={busy ? undefined : () => setOpen(false)}>{t('Refund on Amazon')}</ModalHeader>
        <ModalBody>
          <p>{t('Issue a refund on Amazon through Rithum. Select the items being returned. Inventory will not be restocked.')}</p>
          <p className="text-muted">{t('Available for fully shipped, seller-fulfilled USD orders. Eligibility and remaining balances are checked when you preview.')}</p>
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          {!attempt && (
            <form onSubmit={getPreview}>
              <fieldset disabled={locked}>
                {(sale.items || []).map((item) => (
                  <div className="row align-items-center mb-2" key={item.id}>
                    <div className="col-sm-8">
                      <label htmlFor={`amazon-qty-${item.id}`}>
                        {item.listing?.sku || item.id}
                        {' — '}
                        {t('Returned quantity')}
                        {` (${t('ordered')}: ${item.qty})`}
                      </label>
                    </div>
                    <div className="col-sm-4">
                      <input
                        className="form-control"
                        id={`amazon-qty-${item.id}`}
                        type="number"
                        min="1"
                        max={item.qty}
                        step="1"
                        placeholder={t('Not returned')}
                        value={quantities[item.id] || ''}
                        onChange={(event) => {
                          invalidate();
                          setQuantities({ ...quantities, [item.id]: event.target.value });
                        }}
                      />
                    </div>
                  </div>
                ))}
                <div className="mb-3">
                  <label htmlFor="amazon-refund-amount">{t('Refund amount override (optional)')}</label>
                  <input
                    id="amazon-refund-amount"
                    className="form-control"
                    type="number"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(event) => { invalidate(); setAmount(event.target.value); }}
                  />
                  <small>{t('Leave blank to use the order total minus $9.95, capped by the selected items’ remaining balance.')}</small>
                </div>
                <Button type="submit" color="secondary">{t(busy ? 'Loading…' : 'Preview refund')}</Button>
              </fieldset>
            </form>
          )}
          {quote && (
            <div className="border rounded p-3 mt-3">
              <h5>{t('Refund review')}</h5>
              <dl className="row mb-2">
                <dt className="col-8">{t('Order total')}</dt>
                <dd className="col-4">{money(quote.order_total)}</dd>
                <dt className="col-8">{t('Remaining order balance')}</dt>
                <dd className="col-4">{money(quote.remaining_order_amount)}</dd>
                <dt className="col-8">{t('Maximum for this selection')}</dt>
                <dd className="col-4">{money(quote.maximum_amount)}</dd>
                <dt className="col-8">{t('Verified Amazon amount')}</dt>
                <dd className="col-4">{quote.amazon_refund_amount === null ? t('Not supplied') : money(quote.amazon_refund_amount)}</dd>
                <dt className="col-8">{t('Refund total (USD)')}</dt>
                <dd className="col-4 fw-bold">{money(quote.amount)}</dd>
              </dl>
              <ul>
                {quote.lines.map((line) => (
                  <li key={line.sale_item_id}>
                    {sale.items.find((item) => item.id === line.sale_item_id)?.listing?.sku
                      || line.sale_item_id}
                    {` × ${line.quantity}: ${money(Object.values(line.adjustments).reduce((sum, value) => sum + Number(value), 0))}`}
                  </li>
                ))}
              </ul>
              {!attempt && (
                <Button
                  color="danger"
                  disabled={busy || Number(quote.amount) <= 0}
                  onClick={() => submit({ key: window.crypto.randomUUID(), body: preview.body })}
                >
                  {t('Confirm refund on Amazon')}
                  {' — '}
                  {money(quote.amount)}
                </Button>
              )}
            </div>
          )}
          {attempt && (
            <div className="mt-3" role="status">
              <p>
                {result?.status === 'submitted'
                  ? t('Submitted to Rithum. Amazon has not yet finalized the refund. Completed refunds appear in the refund log after confirmation.')
                  : t('This request is pending or needs review. Check its status and reconcile any uncertain items in Rithum before another refund.')}
              </p>
              {attempt.requestId && <p>{`${t('Request ID')}: ${attempt.requestId}`}</p>}
              {result?.lines && (
                <ul>
                  {result.lines.map((line) => (
                    <li key={line.sale_item_id}>
                      {`${t('Item')} ${line.sale_item_id}: ${line.status}`}
                      {line.seller_adjustment_id && ` (${line.seller_adjustment_id})`}
                      {line.error && ` — ${line.error}`}
                    </li>
                  ))}
                </ul>
              )}
              <Button color="secondary" disabled={busy} onClick={checkStatus}>{t(busy ? 'Checking…' : 'Check request status')}</Button>
              {result?.status === 'submitted' && (
                <Button
                  className="ms-2"
                  color="link"
                  disabled={busy}
                  onClick={() => {
                    saveAttempt(null);
                    setPreview(null);
                    setResult(null);
                    setQuantities(defaultQuantities());
                    setAmount(defaultAmount());
                  }}
                >
                  {t('Start another return')}
                </Button>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
}

AmazonRefund.propTypes = {
  sale: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};
